
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { CustomShoppingItem } from '@/types/shoppingList';
import { toast } from 'sonner';

interface CustomItemsByCategory {
  [category: string]: CustomShoppingItem[];
}

export const useCustomShoppingItems = () => {
  const { user } = useUser();
  const [customItems, setCustomItems] = useState<CustomItemsByCategory>({});
  const [inactiveItems, setInactiveItems] = useState<CustomShoppingItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [familyId, setFamilyId] = useState<string | null>(null);

  // Fetch family ID
  const fetchFamilyId = useCallback(async () => {
    if (!user) return null;
    
    const { data } = await supabase
      .from('profiles')
      .select('current_family_id')
      .eq('id', user.id)
      .single();
    
    return data?.current_family_id || null;
  }, [user]);

  // Load custom items from database
  const loadCustomItems = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const currentFamilyId = await fetchFamilyId();
      
      if (!currentFamilyId) {
        console.error('No family ID found');
        return;
      }
      
      setFamilyId(currentFamilyId);
      
      const { data, error } = await supabase
        .from('shopping_list_custom_items')
        .select('*')
        .eq('family_id', currentFamilyId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Group active items by category
      const activeByCategory: CustomItemsByCategory = {};
      const inactive: CustomShoppingItem[] = [];
      
      data?.forEach(item => {
        const customItem: CustomShoppingItem = {
          id: item.id,
          name: item.name,
          quantity: item.quantity || undefined,
          category: item.category,
          note: item.note || undefined,
          checked: item.checked,
          isActive: item.is_active,
          lastUsedAt: item.last_used_at || undefined,
        };
        
        if (item.is_active) {
          if (!activeByCategory[item.category]) {
            activeByCategory[item.category] = [];
          }
          activeByCategory[item.category].push(customItem);
        } else {
          inactive.push(customItem);
        }
      });
      
      setCustomItems(activeByCategory);
      setInactiveItems(inactive);
    } catch (err) {
      console.error('Error loading custom items:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchFamilyId]);

  // Add a new custom item
  const addCustomItem = useCallback(async (
    name: string,
    category: string,
    quantity?: string,
    note?: string
  ) => {
    if (!user || !familyId) {
      toast.error('Unable to add item');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('shopping_list_custom_items')
        .insert({
          family_id: familyId,
          user_id: user.id,
          name,
          category,
          quantity: quantity || null,
          note: note || null,
          checked: false,
          is_active: true,
          last_used_at: new Date().toISOString(),
        })
        .select()
        .single();
      
      if (error) throw error;
      
      const newItem: CustomShoppingItem = {
        id: data.id,
        name: data.name,
        quantity: data.quantity || undefined,
        category: data.category,
        note: data.note || undefined,
        checked: data.checked,
        isActive: data.is_active,
        lastUsedAt: data.last_used_at || undefined,
      };
      
      setCustomItems(prev => ({
        ...prev,
        [category]: [...(prev[category] || []), newItem],
      }));
      
      toast.success('Item added');
    } catch (err) {
      console.error('Error adding custom item:', err);
      toast.error('Failed to add item');
    }
  }, [user, familyId]);

  // Remove (deactivate) a custom item
  const removeCustomItem = useCallback(async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('shopping_list_custom_items')
        .update({ 
          is_active: false, 
          last_used_at: new Date().toISOString(),
          checked: false 
        })
        .eq('id', itemId);
      
      if (error) throw error;
      
      // Find and move the item to inactive
      let removedItem: CustomShoppingItem | null = null;
      
      setCustomItems(prev => {
        const updated = { ...prev };
        for (const category in updated) {
          const index = updated[category].findIndex(item => item.id === itemId);
          if (index > -1) {
            removedItem = { ...updated[category][index], isActive: false, checked: false };
            updated[category] = updated[category].filter(item => item.id !== itemId);
            if (updated[category].length === 0) {
              delete updated[category];
            }
            break;
          }
        }
        return updated;
      });
      
      if (removedItem) {
        setInactiveItems(prev => [...prev, removedItem!]);
      }
      
      toast.success('Item removed');
    } catch (err) {
      console.error('Error removing custom item:', err);
      toast.error('Failed to remove item');
    }
  }, []);

  // Reactivate an inactive item
  const reactivateItem = useCallback(async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('shopping_list_custom_items')
        .update({ 
          is_active: true, 
          checked: false,
          last_used_at: new Date().toISOString() 
        })
        .eq('id', itemId);
      
      if (error) throw error;
      
      // Find and move the item to active
      const itemToReactivate = inactiveItems.find(item => item.id === itemId);
      
      if (itemToReactivate) {
        setInactiveItems(prev => prev.filter(item => item.id !== itemId));
        
        const reactivatedItem: CustomShoppingItem = {
          ...itemToReactivate,
          isActive: true,
          checked: false,
        };
        
        setCustomItems(prev => ({
          ...prev,
          [reactivatedItem.category]: [...(prev[reactivatedItem.category] || []), reactivatedItem],
        }));
      }
      
      toast.success('Item added to list');
    } catch (err) {
      console.error('Error reactivating item:', err);
      toast.error('Failed to add item');
    }
  }, [inactiveItems]);

  // Toggle checked status for a custom item
  const toggleCustomItemChecked = useCallback(async (itemId: string, checked: boolean) => {
    try {
      const { error } = await supabase
        .from('shopping_list_custom_items')
        .update({ checked })
        .eq('id', itemId);
      
      if (error) throw error;
      
      setCustomItems(prev => {
        const updated = { ...prev };
        for (const category in updated) {
          const index = updated[category].findIndex(item => item.id === itemId);
          if (index > -1) {
            updated[category] = [...updated[category]];
            updated[category][index] = { ...updated[category][index], checked };
            break;
          }
        }
        return updated;
      });
    } catch (err) {
      console.error('Error toggling custom item:', err);
    }
  }, []);

  // Get all unique categories from active custom items
  const getCustomCategories = useCallback(() => {
    return Object.keys(customItems);
  }, [customItems]);

  // Load items on mount
  useEffect(() => {
    loadCustomItems();
  }, [loadCustomItems]);

  return {
    customItems,
    inactiveItems,
    isLoading,
    addCustomItem,
    removeCustomItem,
    reactivateItem,
    toggleCustomItemChecked,
    getCustomCategories,
    loadCustomItems,
  };
};
